import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from '../hooks/use-toast';
import { ArrowRight, CheckCircle, TrendingUp, Users, Zap } from 'lucide-react';
import LiveBiddingShowcase from './LiveBiddingShowcase';

const HeroUltimate = () => {
  const navigate = useNavigate();
  const [regNumber, setRegNumber] = useState('');
  const [kilometers, setKilometers] = useState('');
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [recentSales, setRecentSales] = useState(12);
  const [activeUsers, setActiveUsers] = useState(47);

  // Simulate real-time activity
  useEffect(() => {
    const salesInterval = setInterval(() => {
      setRecentSales(prev => prev + Math.floor(Math.random() * 3));
    }, 8000);

    const usersInterval = setInterval(() => {
      setActiveUsers(prev => Math.max(30, prev + (Math.random() > 0.5 ? 1 : -1)));
    }, 5000);

    return () => {
      clearInterval(salesInterval);
      clearInterval(usersInterval);
    };
  }, []);

  const calculateEstimate = () => {
    if (kilometers && parseInt(kilometers) > 0) {
      setIsCalculating(true);
      setTimeout(() => {
        const km = parseInt(kilometers);
        const base = 280000;
        const estimate = Math.max(50000, base - (km / 10000) * 5000);
        setEstimatedPrice(Math.round(estimate));
        setIsCalculating(false);
      }, 600);
    } else {
      setEstimatedPrice(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!regNumber || !kilometers) {
      toast({
        title: "Vennligst fyll ut begge feltene",
        variant: "destructive"
      });
      return;
    }
    if (regNumber.length < 5) {
      toast({
        title: "Ugyldig registreringsnummer",
        description: "Vennligst sjekk at du har skrevet riktig",
        variant: "destructive"
      });
      return;
    }
    navigate('/selg-bilen', { state: { regNumber, kilometers } });
  };

  return (
    <section className="bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(0 0 0) 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left: Content */}
          <div className="max-w-xl">
            {/* Live activity indicator */}
            <div className="inline-flex flex-wrap items-center gap-2 sm:gap-3 bg-white border border-gray-200 rounded-full px-3 sm:px-4 py-2 mb-4 sm:mb-6 text-xs sm:text-sm">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#fefe94' }}></div>
                  <div className="absolute inset-0 w-2 h-2 rounded-full animate-ping" style={{ backgroundColor: '#fefe94' }}></div>
                </div>
                <span className="font-medium text-gray-900">{activeUsers} online nå</span>
              </div>
              <div className="w-px h-4 bg-gray-200 hidden sm:block"></div>
              <div className="flex items-center gap-2">
                <span className="text-gray-600">{recentSales} solgt i dag</span>
              </div>
            </div>

            {/* Main headline */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-[1.1]">
              Selg bilen din
              <span className="block text-gray-900">enkelt og trygt</span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
              Over 2 000 forhandlere konkurrerer om å gi deg best pris. Vi tar oss av alt.
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit} className="mb-4 sm:mb-6">
              <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6">
                <div className="grid sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div className="group">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reg.nr.
                    </label>
                    <Input
                      type="text"
                      placeholder="AB12345"
                      value={regNumber}
                      onChange={(e) => setRegNumber(e.target.value.toUpperCase())}
                      className="h-12 sm:h-14 text-base sm:text-lg uppercase"
                      autoFocus
                    />
                  </div>
                  <div className="group">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kilometer
                    </label>
                    <Input
                      type="number"
                      placeholder="150 000"
                      value={kilometers}
                      onChange={(e) => {
                        setKilometers(e.target.value);
                      }}
                      onBlur={calculateEstimate}
                      className="h-12 sm:h-14 text-base sm:text-lg"
                    />
                  </div>
                </div>
                
                {/* Instant estimate */}
                {isCalculating && (
                  <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-gray-50 border border-gray-200 rounded-xl animate-pulse">
                    <div className="h-4 bg-gray-300 rounded w-1/3 mb-2"></div>
                    <div className="h-6 sm:h-8 bg-gray-300 rounded w-2/3"></div>
                  </div>
                )}
                
                {!isCalculating && estimatedPrice && (
                  <div className="mb-3 sm:mb-4 p-4 sm:p-5 bg-gray-50 border border-gray-200 rounded-xl animate-fade-in">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 mb-1">
                          Estimert verdi
                        </p>
                        <p className="text-3xl sm:text-4xl font-black text-gray-900">
                          {estimatedPrice.toLocaleString('nb-NO', { style: 'currency', currency: 'NOK', minimumFractionDigits: 0 })}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Estimat • Endelig pris etter sjekk av bilen</p>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full h-12 sm:h-14 bg-gray-900 hover:bg-gray-800 text-white text-base sm:text-lg font-semibold group"
                >
                  <span>Få pris på bilen</span>
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                
                <p className="text-xs text-gray-500 text-center mt-3">
                  Helt gratis • Ingen binding • Tar 2 minutter
                </p>

                {/* I samarbeid med */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-xs text-gray-500 text-center mb-4">I samarbeid med</p>
                  <div className="flex items-center justify-center gap-6 sm:gap-8 flex-wrap">
                    {/* Finn.no logo */}
                    <a 
                      href="https://finn.no" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="opacity-70 hover:opacity-100 transition-opacity"
                      title="Finn.no"
                    >
                      <img 
                        src="https://customer-assets.emergentagent.com/job_534ce381-9f02-4e26-9a94-92aa4a2cb862/artifacts/m65xfhj7_Finn.no-logo.svg.png" 
                        alt="Finn.no" 
                        className="h-6 sm:h-7 w-auto"
                      />
                    </a>
                    
                    {/* NAF logo */}
                    <a 
                      href="https://naf.no" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="opacity-70 hover:opacity-100 transition-opacity"
                      title="NAF - Norges Automobil-Forbund"
                    >
                      <img 
                        src="https://customer-assets.emergentagent.com/job_534ce381-9f02-4e26-9a94-92aa4a2cb862/artifacts/zjdk0vkv_naf_logo_tekst.jpg" 
                        alt="NAF" 
                        className="h-6 sm:h-7 w-auto"
                      />
                    </a>
                    
                    {/* Viking Kontroll logo */}
                    <a 
                      href="https://vikingkontroll.no" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="opacity-70 hover:opacity-100 transition-opacity"
                      title="Viking Kontroll"
                    >
                      <div className="flex flex-col items-center leading-none">
                        <span className="text-red-600 font-bold text-lg sm:text-xl">VIKING</span>
                        <span className="text-red-600 text-[10px] sm:text-xs font-medium tracking-wider">KONTROLL</span>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Right: Live Bidding Showcase */}
          <div className="mt-12 lg:mt-0">
            <LiveBiddingShowcase />
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </section>
  );
};

export default HeroUltimate;